import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import echarts from '../config'
import { cssVar } from '../../../../utils/cssVar'
import FadeIn from '../../../molecules/Transitions/FadeIn'

function applyChartConfig (data, options = {}) {
  const chartOptions = options?.chartOptions ?? {}

  return {
    textStyle: {
      fontFamily: chartOptions?.textStyle?.fontFamily ?? cssVar('--font-sans')
    },
    tooltip: {
      trigger: 'axis',
      showDelay: 0,
      transitionDuration: 0.2,
      formatter: options.valueFormat ? (params) => {
        return `${params.seriesName}<br />${options.categoryFormat(params.name)} ${options.valueFormat(params.value)}`
      } : undefined
    },
    grid: {
      left: 10,
      right: 10,
      top: 20,
      bottom: 30,
      containLabel: true
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: options.categoryFormat,
        rotate: chartOptions.categoryRotate || 30,
        interval: chartOptions.interval ?? 1
      },
      splitNumber: 12
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: options.valueFormat
      }
    },
    series: [
      {
        showSymbol: false,
        name: chartOptions?.name ?? 'Series',
        type: 'line',
        data: data.series.map(d => [d.x, d.y ? Number(d?.y) : null]),
        lineStyle: {
          width: 3
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#5123c9'
            },
            {
              offset: 1,
              color: 'rgba(255, 255, 255, 0)'
            }
          ])
        },

        animate: false,
        animationDuration: 200,
        animationEasing: 'quarticOut', // smoother vertical slide-in
        animationDelay: (index) => index * 10, // ripple from left
        animationDurationUpdate: 500,
        animationEasingUpdate: 'cubicOut',
        animationDelayUpdate: (index) => index * 5
      }
    ]
  }
}

function BasicLineChart ({ height, width, valueFormat, categoryFormat, data, chartOptions }) {
  const chartRef = useRef(null)
  const chartInstanceRef = useRef(null)

  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current)
      window.addEventListener('resize', chartInstanceRef.current.resize)
    }

    return () => {
      if (chartInstanceRef.current) {
        window.removeEventListener('resize', chartInstanceRef.current.resize)
        chartInstanceRef.current.dispose()
        chartInstanceRef.current = null
      }
    }
  }, [])

  const setOptions = useCallback(() => {
    if (chartInstanceRef.current && data) {
      const chart = chartInstanceRef.current
      chart.setOption(applyChartConfig(data || [], {
        valueFormat,
        categoryFormat,
        chartOptions
      }))

      return true
    }
  }, [data, valueFormat, categoryFormat, chartOptions])

  useEffect(() => {
    setOptions()
  }, [setOptions, data])

  return (
    <FadeIn style={{ height, width }}>
      <div style={{ height, width }} ref={chartRef} />
    </FadeIn>
  )
}

BasicLineChart.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  data: PropTypes.shape({
    series: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.any,
      y: PropTypes.any
    })),
    extents: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    })
  }),
  valueFormat: PropTypes.func,
  categoryFormat: PropTypes.func,
  chartOptions: PropTypes.object
}

export default BasicLineChart
